import { IonCard, IonCardContent } from "@ionic/react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import WithNavigation from "../../hoc/with-navigation";
import { useSystem } from "../../store/system";
import { useEffect } from "react";
import { printNumber } from "../../utils/helpers";
import { ClothsConnectionType, Cuts } from "./types";
import {
	bottomLength,
	calculateLeftCut,
	calculateRightCut,
	calculateSymmetricCut,
	clothArea,
	clothWeight,
	concatCutTypes,
	getRelativePoint,
	threadArea,
	topLength,
} from "./functions";
import ClothsForm from "./form";
import { resolveImage } from "./helpers";

import "./styles.css";


const FORM_ID = "cloths";

const Cloths = () => {
	const { systemId } = useParams<any>();
	const systemStore = useSystem();
	const { t } = useTranslation();

	const result = systemStore.formResult || {};
	const fields = systemStore.formFields;
	const img = resolveImage(
		fields?.cut,
		fields?.bottomNetCount,
		fields?.topNetCount,
		result?.leftCut,
		result?.rightCut,
		fields?.netType,
	);

	let leftLength, rightLength;

	const submit = (data) => {
		leftLength = getRelativePoint(
			ClothsConnectionType.linf,
			ClothsConnectionType.lsup,
			data.cut,
			data.netLength,
			data.topNetCount,
			data.bottomNetCount,
			data.firstParam,
			data.secondParam,
			data.netCountVt,
			data.sign,
			data.cutType,
			data.u1,
			data.u2
		).norm();
		rightLength = getRelativePoint(
			ClothsConnectionType.rinf,
			ClothsConnectionType.rsup,
			data.cut,
			data.netLength,
			data.topNetCount,
			data.bottomNetCount,
			data.firstParam,
			data.secondParam,
			data.netCountVt,
			data.sign,
			data.cutType,
			data.u1,
			data.u2
		).norm();

		let leftCut, rightCut;

		if (data.cut === Cuts.FIXED_LEFT) {
			const { cutType, firstParamCut, secondParamCut } = calculateRightCut(
				data.cutType,
				data.firstParam,
				data.secondParam,
				data.netCountVt,
				data.bottomNetCount,
				data.topNetCount,
				data.sign
			);
			leftCut = concatCutTypes(data.cutType, data.firstParam, data.secondParam);
			rightCut = concatCutTypes(cutType, firstParamCut, secondParamCut);
		}
		if (data.cut === Cuts.FIXED_RIGHT) {
			const { cutType, firstParamCut, secondParamCut } = calculateLeftCut(
				data.cutType,
				data.firstParam,
				data.secondParam,
				data.netCountVt,
				data.bottomNetCount,
				data.topNetCount,
				data.sign
			);

			rightCut = concatCutTypes(
				data.cutType,
				data.firstParam,
				data.secondParam
			);
			leftCut = concatCutTypes(cutType, firstParamCut, secondParamCut);
		}
		if (data.cut === Cuts.SYMMETRICAL) {
			const { cutType, firstParamCut, secondParamCut } = calculateSymmetricCut(
				data.topNetCount,
				data.bottomNetCount,
				data.netCountVt
			);

			rightCut = concatCutTypes(cutType, firstParamCut, secondParamCut);
			leftCut = rightCut;
		}

		const _clothArea = clothArea(
			data.topNetCount,
			data.bottomNetCount,
			data.netCountVt,
			data.netLength,
			data.u1,
			data.u2
		);
		const _threadArea = threadArea(
			data.threadDiameter,
			data.topNetCount,
			data.bottomNetCount,
			data.netCountVt,
			data.netLength,
			data.u2
		);
		const _clothWeight = clothWeight(
			data.topNetCount,
			data.bottomNetCount,
			data.netCountVt,
			data.netLength,
			data.runnage,
			data.threadDiameter
		);
		const result = {
			top: topLength(data.topNetCount, data.netLength, data.u1),
			bottom: bottomLength(data.bottomNetCount, data.netLength, data.u1),
			clothArea: _clothArea,
			threadArea: _threadArea,
			clothWeight: _clothWeight,
			leftCut,
			rightCut,
			leftLength,
			rightLength,
		};

		systemStore.submitFormResult(systemId, FORM_ID, {
			...data,
			_evaluationResult: result,
		});
	};

	useEffect(() => {
		systemStore.loadForm(FORM_ID);
		systemStore.loadFormResult(systemId, FORM_ID);
	}, []);

	return (
		<>
			<WithNavigation
				hintsContent={{
					title: systemStore.currentForm?.hints.title,
					htmlDef: systemStore.currentForm?.hints?.htmlDef || "",
				}}
				preventGoBack={false}
				text={t("system.cloths.title")}
			>
				<div className="form">
					{systemStore.currentForm && (
						<>
							{systemStore.formResultReqInfo?.finished && (
								<ClothsForm
									defaultValues={systemStore.currentFormValues}
									onCutChange={console.log}
									onSubmit={submit}
								/>
							)}
							<IonCard>
								<IonCardContent>
									{img && (
										<div className="result-img">
											<div className="floating-row top">
												<span className="left finfo">
													{printNumber(result.leftLength, 2)} m
												</span>
												<span className="right finfo">
													{printNumber(result.rightLength, 2)} m
												</span>
											</div>
											<div className="floating-row bottom">
												<span className="left finfo">{result.leftCut}</span>
												<span className="right finfo">{result.rightCut}</span>
											</div>
											<p
												style={{
													marginBottom: 10,
													textAlign: "center",
													fontWeight: 700,
												}}
											>
												{printNumber(result.top, 2)} m
											</p>
											<div className="img-wrapper">
												<img src={`/assets/${img}.jpeg`} />
											</div>
											<p
												style={{
													marginTop: 5,
													textAlign: "center",
													fontWeight: 700,
												}}
											>
												{printNumber(result.bottom, 2)} m
											</p>
										</div>
									)}
									<div className="ion-text-center">
										<div className="result-row">
											<p className="label">{t("system.cloths.clothArea")}</p>
											<p className="row-value">
												{printNumber(result.clothArea, 1)}
											</p>
											<p>[m²]</p>
										</div>
										<div className="result-row">
											<p className="label">{t("system.cloths.threadArea")}</p>
											<p className="row-value">
												{printNumber(result.threadArea, 1)}
											</p>
											<p>[m²]</p>
										</div>
										<div className="result-row">
											<p className="label">{t("system.cloths.clothWeight")}</p>
											<p className="row-value">
												{printNumber(result.clothWeight, 1)}
											</p>
											<p>[kg]</p>
										</div>
									</div>
								</IonCardContent>
							</IonCard>
						</>
					)}
				</div>
			</WithNavigation>
		</>
	);
};

export default Cloths;
